import { List } from "antd";
import { createElement, type ReactNode } from "react";

type ComponentAdapterContext = {
  mode?: "edit" | "preview";
  runtimeContext?: Record<string, unknown>;
  hasChildren?: boolean;
  renderChildren?: (runtimeContext?: Record<string, unknown>) => ReactNode;
  runRequestEvent: (
    requestEvent: string,
    payload: Record<string, unknown>,
  ) => Promise<unknown>;
};

type ComponentAdapter = {
  adaptProps: (
    props: Record<string, unknown>,
    context: ComponentAdapterContext,
  ) => Record<string, unknown>;
};

export type ListItemRecord = unknown;

export function normalizeListDataSource(dataSource: unknown): ListItemRecord[] {
  return Array.isArray(dataSource) ? dataSource : [];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeGridNumber(value: unknown): number | undefined {
  if (typeof value !== "number") return undefined;
  if (!Number.isFinite(value)) return undefined;
  return value;
}

export function normalizeListGrid(
  grid: unknown,
): Record<string, unknown> | undefined {
  if (!isPlainObject(grid)) return undefined;
  const nextGrid = Object.entries(grid).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      const nextValue = normalizeGridNumber(value);
      if (nextValue !== undefined) {
        acc[key] = nextValue;
      }
      return acc;
    },
    {},
  );
  return Object.keys(nextGrid).length > 0 ? nextGrid : undefined;
}

export function getListItemKey(
  record: ListItemRecord,
  index: number,
  rowKey?: unknown,
): string {
  const keyField = String(rowKey || "").trim();
  if (
    keyField &&
    record &&
    typeof record === "object" &&
    !Array.isArray(record)
  ) {
    const value = (record as Record<string, unknown>)[keyField];
    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }
  }
  return String(index);
}

export function buildListItemRuntimeContext(
  runtimeContext: Record<string, unknown> | undefined,
  record: ListItemRecord,
  index: number,
  rowKey: string,
  list: ListItemRecord[],
): Record<string, unknown> {
  const baseData =
    runtimeContext?.data &&
    typeof runtimeContext.data === "object" &&
    !Array.isArray(runtimeContext.data)
      ? (runtimeContext.data as Record<string, unknown>)
      : {};

  return {
    ...(runtimeContext || {}),
    data: {
      ...baseData,
      record,
      item: record,
      index,
      rowKey,
      list,
    },
  };
}

export const listTemplateAdapter: ComponentAdapter = {
  adaptProps: (props, context) => {
    const {
      children: _children,
      rowKey,
      previewCount,
      grid,
      ...restProps
    } = props;
    const safeGrid = normalizeListGrid(grid);
    const normalizedProps = safeGrid
      ? { ...restProps, grid: safeGrid }
      : restProps;
    if (!context.hasChildren || !context.renderChildren) {
      return normalizedProps;
    }

    void previewCount;
    const rows = normalizeListDataSource(props.dataSource);
    const rowKeyField = String(rowKey || "").trim();

    return {
      ...normalizedProps,
      dataSource: rows,
      renderItem: (record: unknown, rowIndex: number) => {
        const itemKey = getListItemKey(record, rowIndex, rowKeyField);
        const nextRuntimeContext = buildListItemRuntimeContext(
          context.runtimeContext,
          record,
          rowIndex,
          itemKey,
          rows,
        );
        return createElement(
          List.Item,
          { key: itemKey },
          context.renderChildren?.(nextRuntimeContext),
        );
      },
    };
  },
};
